import { useUserInfo } from '@/features/auth/hooks/useUserInfo';
import { PDT_LINKS, PORTAL_ID_LINKS } from '@/features/home-v2/constants/links';
import type { ChangeAppTarget } from '@/shared/hooks/useChangeApp';
import type { PlatformInfo } from '@/features/auth/types/auth.types';
import { isFederatedAccessNotGranted } from '@/shared/utils/apiError';

/**
 * Resolution of the post-login destination (RN07).
 *
 * - `loading` — user info still being fetched; render a loading state, never
 *   an intermediate flash of the home
 * - `redirect` — the user has access to exactly ONE platform: go straight to
 *   it, the unified home is not shown (there is no choice to make)
 * - `home` — more than one platform (or none resolvable): the unified home
 *   is where the user chooses where to go
 * - `access-not-granted` — authentication succeeded, but none of the user's
 *   accessible business accounts has a platform association
 */
export type PostLoginDestination =
  | { status: 'loading' }
  | { status: 'home' }
  | { status: 'access-not-granted' }
  | { status: 'redirect'; target: ChangeAppTarget; platformName: string };

/**
 * Maps a platform from `GET /auth/user-info` (businessAccount.platforms) to a
 * known cross-portal change-app target.
 *
 * A chave preferencial é o `client_id` **cadastrado no banco**
 * (`portal.platforms.client_id`), comparado com os client ids dos portais
 * conhecidos: é exato e não depende de como a plataforma foi nomeada. O nome
 * normalizado permanece como fallback para plataformas cadastradas sem
 * `client_id`. Plataformas não reconhecidas resolvem `null` e o chamador cai
 * na home unificada (fail-safe: mostrar a home é sempre recuperável, um
 * redirecionamento errado não é).
 */
export function resolvePlatformTarget(
  platform: PlatformInfo
): ChangeAppTarget | null {
  const pdtClientId = import.meta.env.VITE_PDT_CLIENT_ID;
  const portalIdClientId = import.meta.env.VITE_PORTAL_ID_CLIENT_ID;
  const clientId = platform.client_id?.trim();

  if (clientId) {
    if (pdtClientId && clientId === pdtClientId) {
      return { url: PDT_LINKS.HOME, targetClientId: clientId };
    }
    if (portalIdClientId && clientId === portalIdClientId) {
      return { url: PORTAL_ID_LINKS.HOME, targetClientId: clientId };
    }
  }

  const normalized = platform.name?.trim().toLowerCase() ?? '';
  if (!normalized) return null;

  if (normalized.includes('pdt') || normalized.includes('data trust')) {
    return {
      url: PDT_LINKS.HOME,
      targetClientId: import.meta.env.VITE_PDT_CLIENT_ID,
    };
  }
  if (
    normalized.includes('portal id') ||
    normalized.includes('portal de identidade') ||
    normalized.includes('identidade de fraude')
  ) {
    return {
      url: PORTAL_ID_LINKS.HOME,
      targetClientId: import.meta.env.VITE_PORTAL_ID_CLIENT_ID,
    };
  }
  return null;
}

/**
 * usePostLoginDestination
 *
 * RN07 — destino do usuário após o login, válido para TODAS as formas de
 * acesso (senha, MFA e federado), pois todas navegam para `/` e a decisão
 * acontece uma única vez, no HomeContainer:
 *
 * - acesso a exatamente UMA plataforma → direcionado diretamente a ela (a
 *   home unificada não é exibida: não há escolha a ser feita — CA01);
 * - acesso a mais de uma plataforma → home unificada, onde escolhe para onde
 *   deseja ir (CA02).
 *
 * A fonte é `businessAccount.platforms` do `GET /auth/user-info` (a entidade
 * ativa do usuário). Plataformas não mapeáveis para um portal conhecido caem
 * na home (fail-safe).
 *
 * @returns The resolved post-login destination
 * @since 1.0.0
 * @category Hooks
 */
export function usePostLoginDestination(): PostLoginDestination {
  const { data: userInfo, isLoading, error } = useUserInfo();

  if (isLoading) {
    return { status: 'loading' };
  }

  // O BFF recusou o bootstrap por falta de plataforma nas contas acessíveis.
  // Precisa vir antes da decisão de destino: sem isso o usuário
  // cairia na home vazia, ou seria levado direto a uma plataforma da entidade
  // sem poder fazer nada nela.
  if (isFederatedAccessNotGranted(error)) {
    return { status: 'access-not-granted' };
  }

  const platforms = userInfo?.businessAccount?.platforms ?? [];
  if (platforms.length !== 1) {
    return { status: 'home' };
  }

  const target = resolvePlatformTarget(platforms[0]);
  if (!target) {
    return { status: 'home' };
  }

  return {
    status: 'redirect',
    target,
    platformName: platforms[0].name,
  };
}
